import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { LuUsers, LuPlus, LuPencil, LuTrash2, LuSave, LuX, LuEye, LuUserPlus, LuInfo } from 'react-icons/lu'
import api from '../services/api'
import './UsuariosPage.css'

function BindUsuariosPage() {
  const { isOperator, roleReady } = useAuth()
  
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [selectedUser, setSelectedUser] = useState(null)
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    portal_role: 'principal',
    accounts: []
  })
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState('')
  const [showRoleTooltip, setShowRoleTooltip] = useState(false)

  useEffect(() => {
    if (roleReady && isOperator) {
      loadUsers()
    }
  }, [roleReady, isOperator])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await api.get('/bind-users')
      setUsers(response.data.users || [])
    } catch (err) {
      setError('Error al cargar usuarios')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setModalMode('create')
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      portal_role: 'principal',
      accounts: [{ cvu: '', account_id: '', alias: '' }]
    })
    setFormError('')
    setShowModal(true)
  }

  const openEditModal = async (user) => {
    try {
      setFormLoading(true)
      const response = await api.get(`/bind-users/${user.id}`)
      const userData = response.data.user
      
      setModalMode('edit')
      setSelectedUser(userData)
      setFormData({
        username: userData.username,
        password: '',
        full_name: userData.full_name || '',
        email: userData.email || '',
        portal_role: userData.portal_role || 'principal',
        accounts: userData.accounts.length > 0 
          ? userData.accounts 
          : [{ cvu: '', account_id: '', alias: '' }]
      })
      setFormError('')
      setShowModal(true)
    } catch (err) {
      setError('Error al cargar usuario')
    } finally {
      setFormLoading(false)
    }
  }

  const openViewModal = async (user) => {
    try {
      setFormLoading(true)
      const response = await api.get(`/bind-users/${user.id}`)
      const userData = response.data.user
      
      setModalMode('view')
      setSelectedUser(userData)
      setFormData({
        username: userData.username,
        password: '',
        full_name: userData.full_name || '',
        email: userData.email || '',
        portal_role: userData.portal_role || 'principal',
        accounts: userData.accounts
      })
      setShowModal(true)
    } catch (err) {
      setError('Error al cargar usuario')
    } finally {
      setFormLoading(false)
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedUser(null)
    setFormData({ username: '', password: '', full_name: '', email: '', portal_role: 'principal', accounts: [] })
    setFormError('')
    setShowRoleTooltip(false)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleAccountChange = (index, field, value) => {
    const newAccounts = [...formData.accounts]
    newAccounts[index] = { ...newAccounts[index], [field]: value }
    setFormData(prev => ({ ...prev, accounts: newAccounts }))
  }

  const addAccount = () => {
    setFormData(prev => ({
      ...prev,
      accounts: [...prev.accounts, { cvu: '', account_id: '', alias: '' }]
    }))
  }

  const removeAccount = (index) => {
    if (formData.accounts.length > 1) {
      const newAccounts = formData.accounts.filter((_, i) => i !== index)
      setFormData(prev => ({ ...prev, accounts: newAccounts }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError('')

    try {
      const validAccounts = formData.accounts.filter(acc => acc.cvu && acc.account_id)

      if (modalMode === 'create') {
        await api.post('/bind-users', { ...formData, accounts: validAccounts })
        setSuccess('Usuario creado exitosamente')
      } else if (modalMode === 'edit') {
        await api.put(`/bind-users/${selectedUser.id}`, {
          username: formData.username,
          full_name: formData.full_name,
          email: formData.email,
          portal_role: formData.portal_role,
          password: formData.password || undefined
        })

        for (const acc of selectedUser.accounts) {
          await api.delete(`/bind-users/${selectedUser.id}/accounts/${acc.id}`)
        }
        
        for (const acc of validAccounts) {
          await api.post(`/bind-users/${selectedUser.id}/accounts`, {
            cvu: acc.cvu,
            account_id: acc.account_id,
            alias: acc.alias
          })
        }

        setSuccess('Usuario actualizado exitosamente')
      }

      closeModal()
      loadUsers()
    } catch (err) {
      setFormError(err.response?.data?.error || 'Error al guardar usuario')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Está seguro de eliminar al usuario "${user.username}"? Esta acción no se puede deshacer.`)) {
      return
    }

    try {
      await api.delete(`/bind-users/${user.id}`)
      setSuccess('Usuario eliminado exitosamente')
      loadUsers()
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar usuario')
    }
  }

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => { setSuccess(''); setError('') }, 5000)
      return () => clearTimeout(timer)
    }
  }, [success, error])

  if (!roleReady || !isOperator) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <p className="empty-state-large">Acceso denegado. Solo el operador puede gestionar usuarios.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><LuUsers /> Gestión de Usuarios BIND</h1>
        <button className="btn-primary btn-nuevo-usuario" onClick={openCreateModal}>
          <LuUserPlus /> <span>Nuevo Usuario</span>
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <div className="loading-state">Cargando usuarios...</div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <p>No hay usuarios BIND registrados.</p>
          <p>Haga clic en "Nuevo Usuario" para crear el primero.</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Cuentas Asignadas</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td className="username-cell">{user.username}</td>
                  <td>{user.full_name || '-'}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={`role-badge ${user.portal_role === 'lector' ? 'role-lector' : 'role-principal'}`}>
                      {user.portal_role === 'lector' ? 'Lector' : 'Principal'}
                    </span>
                  </td>
                  <td className="account-count">{user.account_count}</td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button className="btn-icon btn-view" onClick={() => openViewModal(user)} title="Ver detalle"><LuEye /></button>
                    <button className="btn-icon btn-edit" onClick={() => openEditModal(user)} title="Editar"><LuPencil /></button>
                    <button className="btn-icon btn-delete" onClick={() => handleDeleteUser(user)} title="Eliminar"><LuTrash2 /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content user-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'create' && 'Nuevo Usuario BIND'}
                {modalMode === 'edit' && 'Editar Usuario BIND'}
                {modalMode === 'view' && 'Detalle de Usuario BIND'}
              </h2>
              <button className="btn-close" onClick={closeModal}><LuX /></button>
            </div>

            {formError && <div className="alert alert-error">{formError}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>Datos del Usuario</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Usuario *</label>
                    <input type="text" name="username" value={formData.username} onChange={handleInputChange} placeholder="nombre_usuario" disabled={modalMode === 'view'} required />
                  </div>
                  {modalMode !== 'view' && (
                    <div className="form-group">
                      <label>{modalMode === 'create' ? 'Contraseña *' : 'Nueva Contraseña'}</label>
                      <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder={modalMode === 'edit' ? 'Dejar vacío para mantener' : '••••••••'} required={modalMode === 'create'} />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Nombre Completo</label>
                    <input type="text" name="full_name" value={formData.full_name} onChange={handleInputChange} placeholder="Juan Pérez" disabled={modalMode === 'view'} />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="email@ejemplo.com" disabled={modalMode === 'view'} />
                  </div>
                  <div className="form-group">
                    <label className="label-with-tooltip">
                      Rol
                      <button type="button" className="tooltip-trigger" onClick={() => setShowRoleTooltip(!showRoleTooltip)} onMouseEnter={() => setShowRoleTooltip(true)} onMouseLeave={() => setShowRoleTooltip(false)}>
                        <LuInfo />
                      </button>
                      {showRoleTooltip && (
                        <div className="role-tooltip">
                          <strong>Principal:</strong> Permite al usuario hacer uso normal de su cuenta, incluyendo transferencias.
                          <br /><br />
                          <strong>Lector:</strong> NO permite realizar transferencias. Solo puede consultar saldos y movimientos.
                        </div>
                      )}
                    </label>
                    <select name="portal_role" value={formData.portal_role} onChange={handleInputChange} disabled={modalMode === 'view'} className="role-select">
                      <option value="principal">Principal</option>
                      <option value="lector">Lector</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <h3>Cuentas Asignadas</h3>
                  {modalMode !== 'view' && (
                    <button type="button" className="btn-add-account" onClick={addAccount}><LuPlus /> Agregar Cuenta</button>
                  )}
                </div>

                {formData.accounts.length === 0 ? (
                  <p className="no-accounts">No hay cuentas asignadas</p>
                ) : (
                  <div className="accounts-list">
                    {formData.accounts.map((account, index) => (
                      <div key={index} className="account-row">
                        <div className="account-fields">
                          <div className="form-group">
                            <label>CVU *</label>
                            <input type="text" value={account.cvu} onChange={(e) => handleAccountChange(index, 'cvu', e.target.value)} placeholder="CVU (22 dígitos)" disabled={modalMode === 'view'} />
                          </div>
                          <div className="form-group">
                            <label>Account ID *</label>
                            <input type="text" value={account.account_id} onChange={(e) => handleAccountChange(index, 'account_id', e.target.value)} placeholder="20-1-XXXXXXX-1-5" disabled={modalMode === 'view'} />
                          </div>
                          <div className="form-group">
                            <label>Alias</label>
                            <input type="text" value={account.alias || ''} onChange={(e) => handleAccountChange(index, 'alias', e.target.value)} placeholder="mi.alias.cvu" disabled={modalMode === 'view'} />
                          </div>
                        </div>
                        {modalMode !== 'view' && formData.accounts.length > 1 && (
                          <button type="button" className="btn-remove-account" onClick={() => removeAccount(index)}><LuTrash2 /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {modalMode !== 'view' && (
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={closeModal}>Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={formLoading}>
                    <LuSave /> {formLoading ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BindUsuariosPage
